import { PermissionModel } from './authorization';
import { GoalModel } from './goals';
import { Gender } from './shared';
import { AuthenticationMethodModel, UserModel } from './users';

enum AccountType {
  PRODUCER = 'PRODUCER',
  CONSUMER = 'CONSUMER',
  MASTER = 'MASTER'
}

enum AccountProfileLinkType {
  BEHANCE = 'BEHANCE',
  BLOG = 'BLOG',
  FACEBOOK = 'FACEBOOK',
  GITHUB = 'GITHUB',
  INSTAGRAM = 'INSTAGRAM',
  LINKEDIN = 'LINKEDIN',
  PINTEREST = 'PINTEREST',
  SNAPCHAT = 'SNAPCHAT',
  SOUNDCLOUD = 'SOUNDCLOUD',
  TIKTOK = 'TIKTOK',
  VIMEO = 'VIMEO',
  WEBSITE = 'WEBSITE',
  X = 'X',
  YOUTUBE = 'YOUTUBE'
}

interface AccountProfileModel {
  type: AccountType;
  user: {
    id: number;
    name: string;
    pictureUrl?: string;
    createdAt?: string;
  };
  jobTitle: string | null;
  about: string;
  links: {
    type: AccountProfileLinkType;
    name: string;
    url: string;
  }[];
  approvals: {
    privacyPolicyApproved: boolean;
    termsOfServiceApproved: boolean;
    marketingApproved: boolean;
  };
  rating: number;
  createdGoalsCount: number;
  createdGoals: GoalModel[];
  completedGoalsCount: number;
  completedGoals: GoalModel[];
}

enum AffiliateProfileStatus {
  ACTIVE = 'ACTIVE',
  DISABLED = 'DISABLED'
}

interface AffiliateModel {
  referralCode: string | null;
  status: AffiliateProfileStatus;
  // Commission rates the affiliator earns, as percentages (e.g. 15 = 15%).
  firstDirectPercent: number | null;
  firstAssistedPercent: number | null;
  residualDirectPercent: number | null;
  residualAssistedPercent: number | null;
  // The mentor's own share of their goal price (what they keep), as a percentage.
  // samodzielne (independent) vs niesamodzielne (affiliator/imprv-driven) sales.
  mentorMarginPercent: number | null;
  mentorMarginAssistedPercent: number | null;
}

// The backend returns an affiliate profile only for a signed affiliator, and DISABLED suspends one.
const isAffiliator = (affiliate: AffiliateModel | null | undefined): boolean =>
  affiliate?.status === AffiliateProfileStatus.ACTIVE;

type InvoiceBuyerType = 'COMPANY' | 'PRIVATE';

interface AccountModel {
  user: UserModel;
  firstName: string | null;
  lastName: string | null;
  gender: Gender | null;
  introIsDone: boolean;
  approvals?: {
    privacyPolicyApproved: boolean;
    termsOfServiceApproved: boolean;
    marketingApproved: boolean;
  };
  authenticationMethods: AuthenticationMethodModel[];
  affiliate?: AffiliateModel;
  __permissions?: PermissionModel[];
  // Invoice (faktura) identity — set on demand so the user can render invoices via infakt.
  invoiceBuyerType: InvoiceBuyerType | null;
  invoiceName: string | null;
  invoiceTaxId: string | null;
  invoiceCountry: string | null;
  invoiceStreet: string | null;
  invoiceStreetNumber: string | null;
  invoiceCity: string | null;
  invoicePostCode: string | null;
}

interface AccountApprovalCreateModel {
  legalDocumentId: number;
}

interface AccountUpdatePatchModel {
  first_name: string | null;
  last_name: string | null;
  gender: Gender | null;
  // Invoice details. The backend PATCH schema is populate-by-name, so snake_case keys are accepted
  // alongside the existing snake_case fields above.
  invoice_buyer_type?: InvoiceBuyerType | null;
  invoice_name?: string | null;
  invoice_tax_id?: string | null;
  invoice_country?: string | null;
  invoice_street?: string | null;
  invoice_street_number?: string | null;
  invoice_city?: string | null;
  invoice_post_code?: string | null;
}

interface BasicAccountModel {
  userId: number;
  user: UserModel;
  firstName: string | null;
  lastName: string | null;
  gender: Gender | null;
}

export { AccountProfileLinkType, AccountType, AffiliateProfileStatus, isAffiliator };
export type {
  AccountApprovalCreateModel,
  AccountModel,
  AccountProfileModel,
  AccountUpdatePatchModel,
  AffiliateModel,
  BasicAccountModel,
  InvoiceBuyerType
};
